import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { Header, VStack } from 'tio-ui/components/layout';
import { eq } from 'tio-ui/utilities';
import {
  PhPath,
  PhBuildings,
  PhCaretDown,
  PhUserCircle,
  PhEyeSlash,
  PhEye,
  PhMoon,
  PhSun,
  PhSparkle,
  PhWarning,
  PhGraduationCap,
  PhChatCircleDots,
} from 'ember-phosphor-icons';
import MegaMenuNav from 'tio-employee/components/reimagined/mega-menu-nav';
import DataSourceDocs from 'tio-employee/components/reimagined/data-source-docs';
import ReviewDrawer from 'tio-employee/components/reimagined/review-drawer';
import ToastContainer from 'tio-reimagined/components/toast';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type ComponentReviewService from 'tio-employee/services/component-review';
import type ReimaginedToastService from 'tio-employee/services/reimagined-toast';
import {
  DEMO_CLIENT_LIST,
  DEMO_SCENARIO_LIST,
  DEMO_TAS_SCENARIO_LIST,
  getDemoClientIdFromUrl,
  getDemoScenarioIdFromUrl,
  getCurrentDemoTasScenarioId,
  getDemoData,
  getDemoScenario,
  setDemoClientId,
  setDemoScenarioId,
  setDemoTasScenarioId,
  type DemoClientId,
  type DemoScenarioId,
  type DemoTasScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

export default class ReimagainedLayout extends Component {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare componentReview: ComponentReviewService;
  @service('reimagined-toast') declare toast: ReimaginedToastService;

  @tracked selectedClientId: DemoClientId = getDemoClientIdFromUrl();
  @tracked selectedScenarioId: DemoScenarioId = getDemoScenarioIdFromUrl();
  @tracked selectedTasScenarioId: DemoTasScenarioId = getCurrentDemoTasScenarioId();
  @tracked isClientMenuOpen = false;
  @tracked isScenarioMenuOpen = false;
  @tracked isTasScenarioMenuOpen = false;
  @tracked isLegacyFrameHidden = this.initLegacyFrameState();
  @tracked isDarkMode = false;
  @tracked isDemoMode = this.getIsDemoMode();

  constructor(owner: Owner, args: object) {
    super(owner, args);
    // Apply legacy frame state on init
    if (this.isLegacyFrameHidden) {
      this.applyLegacyFrameState(true);
    }
  }

  private initLegacyFrameState() {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('reimagined-legacy-frame-hidden') === 'true';
  }

  private applyLegacyFrameState(hidden: boolean) {
    const legacyBanner = document.querySelector('[data-legacy-banner]');
    const legacySidebar = document.querySelector('[data-legacy-sidebar]');
    if (legacyBanner) {
      legacyBanner.classList.toggle('hidden', hidden);
    }
    if (legacySidebar) {
      legacySidebar.classList.toggle('hidden', hidden);
    }
    document.body.classList.toggle('reimagined-fullscreen', hidden);
  }

  get clients() {
    return DEMO_CLIENT_LIST;
  }

  get scenarios() {
    return DEMO_SCENARIO_LIST;
  }

  get tasScenarioOptions() {
    return DEMO_TAS_SCENARIO_LIST.map((id) => {
      switch (id) {
        case 'not-started':
          return { id, name: 'TA Not Started', description: 'No applications yet' };
        case 'in-progress':
          return { id, name: 'TA In Progress', description: 'Pending or active requests' };
        case 'approved':
          return { id, name: 'TA Approved', description: 'Approved, awaiting payment' };
        case 'rejected':
          return { id, name: 'TA Rejected', description: 'Declined application' };
        case 'completed':
          return { id, name: 'TA Completed', description: 'Paid and fulfilled' };
        default:
          return { id, name: 'TA Scenario', description: '' };
      }
    });
  }

  get selectedClient() {
    return getDemoData(this.selectedClientId);
  }

  get selectedScenario() {
    return getDemoScenario(this.selectedScenarioId);
  }

  get selectedClientName() {
    return this.selectedClient.client.name;
  }

  get selectedClientIndustry() {
    return this.selectedClient.client.industry;
  }

  get selectedScenarioName() {
    return this.selectedScenario.name;
  }

  get selectedTasScenarioName() {
    const option = this.tasScenarioOptions.find((item) => item.id === this.selectedTasScenarioId);
    return option?.name ?? 'TA Scenario';
  }

  get selectedTasScenarioDescription() {
    const option = this.tasScenarioOptions.find((item) => item.id === this.selectedTasScenarioId);
    return option?.description ?? '';
  }

  get selectedScenarioDescription() {
    return this.selectedScenario.description;
  }

  selectClient = (clientId: DemoClientId) => {
    // Use full page reload to ensure all demo data refreshes
    setDemoClientId(clientId);
  };

  selectScenario = (scenarioId: DemoScenarioId) => {
    // Use full page reload to ensure all demo data refreshes
    setDemoScenarioId(scenarioId);
  };

  selectTasScenario = (scenarioId: DemoTasScenarioId) => {
    // Use full page reload to ensure all demo data refreshes
    setDemoTasScenarioId(scenarioId);
  };

  toggleClientMenu = () => {
    this.isClientMenuOpen = !this.isClientMenuOpen;
    this.isScenarioMenuOpen = false;
    this.isTasScenarioMenuOpen = false;
  };

  toggleScenarioMenu = () => {
    this.isScenarioMenuOpen = !this.isScenarioMenuOpen;
    this.isClientMenuOpen = false;
    this.isTasScenarioMenuOpen = false;
  };

  toggleTasScenarioMenu = () => {
    this.isTasScenarioMenuOpen = !this.isTasScenarioMenuOpen;
    this.isClientMenuOpen = false;
    this.isScenarioMenuOpen = false;
  };

  closeClientMenu = () => {
    this.isClientMenuOpen = false;
  };

  closeScenarioMenu = () => {
    this.isScenarioMenuOpen = false;
  };

  closeTasScenarioMenu = () => {
    this.isTasScenarioMenuOpen = false;
  };

  toggleLegacyFrame = () => {
    this.isLegacyFrameHidden = !this.isLegacyFrameHidden;
    this.applyLegacyFrameState(this.isLegacyFrameHidden);
    // Persist to localStorage for page refresh persistence
    localStorage.setItem('reimagined-legacy-frame-hidden', String(this.isLegacyFrameHidden));
  };

  toggleDarkMode = () => {
    this.isDarkMode = !this.isDarkMode;
    // Toggle dark class on html element for CSS variable inheritance
    document.documentElement.classList.toggle('dark', this.isDarkMode);
  };

  // Data audit state is managed by reimaginedData service for reactivity across components

  getIsDemoMode() {
    if (typeof window === 'undefined') {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    return (
      params.get('demo') === 'true' || params.has('demo_client') || params.has('demo_scenario')
    );
  }

  toggleDemoMode = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    if (this.isDemoMode) {
      url.searchParams.delete('demo');
      url.searchParams.delete('demo_client');
      url.searchParams.delete('demo_scenario');
      url.searchParams.delete('demo_ta_scenario');
      // Clear sticky demo state from localStorage
      localStorage.removeItem('reimagined-demo-mode');
      localStorage.removeItem('reimagined-demo-client');
      localStorage.removeItem('reimagined-demo-scenario');
      localStorage.removeItem('reimagined-demo-ta-scenario');
    } else {
      url.searchParams.set('demo', 'true');
      url.searchParams.set('demo_client', this.selectedClientId);
      url.searchParams.set('demo_scenario', this.selectedScenarioId);
      url.searchParams.set('demo_ta_scenario', this.selectedTasScenarioId);
    }

    window.location.assign(url.toString());
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="min-h-screen overflow-hidden bg-white isolate">
      <Header class="relative z-30 border-b py-4 flex-shrink-0 border-gray-200">
        <div class="w-full {{if this.isLegacyFrameHidden 'px-4' 'max-w-7xl mx-auto px-4'}}">
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <PhPath @size={{32}} class="text-violet-600 dark:text-violet-400" />
              <span class="text-2xl font-bold text-gray-900">TIO Reimagined</span>
              <span
                class="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300"
              >DEMO</span>
            </div>

            {{! Client Selector }}
            <div class="flex items-center gap-3">
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center gap-3 px-4 py-2 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors"
                  {{on "click" this.toggleClientMenu}}
                >
                  <PhBuildings @size={{20}} class="text-violet-600" />
                  <div class="text-left">
                    <div class="text-sm font-semibold text-violet-900">
                      {{this.selectedClientName}}
                    </div>
                    <div class="text-xs text-violet-600">{{this.selectedClientIndustry}}</div>
                  </div>
                  <PhCaretDown @size={{16}} class="text-violet-600" />
                </button>

                {{#if this.isClientMenuOpen}}
                  {{! Backdrop }}
                  <button
                    type="button"
                    class="fixed inset-0 z-10"
                    aria-label="Close client menu"
                    {{on "click" this.closeClientMenu}}
                  ></button>

                  {{! Dropdown }}
                  <div
                    class="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden"
                  >
                    <div class="px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Demo
                        Client</span>
                    </div>
                    {{#each this.clients as |client|}}
                      <button
                        type="button"
                        class="w-full flex items-center gap-3 px-4 py-3 hover:bg-violet-50 transition-colors text-left
                          {{if
                            (eq client.id this.selectedClientId)
                            'bg-violet-50 border-l-4 border-violet-600'
                          }}"
                        {{on "click" (fn this.selectClient client.id)}}
                      >
                        <PhBuildings @size={{24}} class="text-gray-400 flex-shrink-0" />
                        <div class="flex-1 min-w-0">
                          <div class="font-semibold text-gray-900">{{client.name}}</div>
                          <div class="text-sm text-gray-500">{{client.industry}}</div>
                          <div class="text-xs text-gray-400 mt-1">
                            {{client.employeeCount}}
                            employees •
                            {{client.taUtilization}}% TA utilization
                          </div>
                        </div>
                        {{#if (eq client.id this.selectedClientId)}}
                          <div class="w-2 h-2 bg-violet-600 rounded-full flex-shrink-0"></div>
                        {{/if}}
                      </button>
                    {{/each}}
                  </div>
                {{/if}}
              </div>

              {{! Scenario Selector }}
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center gap-3 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                  {{on "click" this.toggleScenarioMenu}}
                >
                  <PhUserCircle @size={{20}} class="text-emerald-600" />
                  <div class="text-left">
                    <div class="text-sm font-semibold text-emerald-900">
                      {{this.selectedScenarioName}}
                    </div>
                    <div class="text-xs text-emerald-600">User Scenario</div>
                  </div>
                  <PhCaretDown @size={{16}} class="text-emerald-600" />
                </button>

                {{#if this.isScenarioMenuOpen}}
                  {{! Backdrop }}
                  <button
                    type="button"
                    class="fixed inset-0 z-10"
                    aria-label="Close scenario menu"
                    {{on "click" this.closeScenarioMenu}}
                  ></button>

                  {{! Dropdown }}
                  <div
                    class="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden"
                  >
                    <div class="px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">User
                        Scenario</span>
                    </div>
                    {{#each this.scenarios as |scenario|}}
                      <button
                        type="button"
                        class="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors text-left
                          {{if
                            (eq scenario.id this.selectedScenarioId)
                            'bg-emerald-50 border-l-4 border-emerald-600'
                          }}"
                        {{on "click" (fn this.selectScenario scenario.id)}}
                      >
                        <PhUserCircle @size={{24}} class="text-gray-400 flex-shrink-0" />
                        <div class="flex-1 min-w-0">
                          <div class="font-semibold text-gray-900">{{scenario.name}}</div>
                          <div class="text-sm text-gray-500">{{scenario.description}}</div>
                        </div>
                        {{#if (eq scenario.id this.selectedScenarioId)}}
                          <div class="w-2 h-2 bg-emerald-600 rounded-full flex-shrink-0"></div>
                        {{/if}}
                      </button>
                    {{/each}}
                  </div>
                {{/if}}
              </div>

              {{! TAS Scenario Selector }}
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center gap-3 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                  {{on "click" this.toggleTasScenarioMenu}}
                >
                  <PhGraduationCap @size={{20}} class="text-amber-600" />
                  <div class="text-left">
                    <div class="text-sm font-semibold text-amber-900">
                      {{this.selectedTasScenarioName}}
                    </div>
                    <div class="text-xs text-amber-600">TA Scenario</div>
                  </div>
                  <PhCaretDown @size={{16}} class="text-amber-600" />
                </button>

                {{#if this.isTasScenarioMenuOpen}}
                  {{! Backdrop }}
                  <button
                    type="button"
                    class="fixed inset-0 z-10"
                    aria-label="Close TA scenario menu"
                    {{on "click" this.closeTasScenarioMenu}}
                  ></button>

                  {{! Dropdown }}
                  <div
                    class="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden"
                  >
                    <div class="px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">TA
                        Scenario</span>
                    </div>
                    {{#each this.tasScenarioOptions as |scenario|}}
                      <button
                        type="button"
                        class="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 transition-colors text-left
                          {{if
                            (eq scenario.id this.selectedTasScenarioId)
                            'bg-amber-50 border-l-4 border-amber-600'
                          }}"
                        {{on "click" (fn this.selectTasScenario scenario.id)}}
                      >
                        <PhGraduationCap @size={{24}} class="text-gray-400 flex-shrink-0" />
                        <div class="flex-1 min-w-0">
                          <div class="font-semibold text-gray-900">{{scenario.name}}</div>
                          <div class="text-sm text-gray-500">{{scenario.description}}</div>
                        </div>
                        {{#if (eq scenario.id this.selectedTasScenarioId)}}
                          <div class="w-2 h-2 bg-amber-600 rounded-full flex-shrink-0"></div>
                        {{/if}}
                      </button>
                    {{/each}}
                  </div>
                {{/if}}
              </div>

              {{! Demo Control Buttons }}
              <div class="flex items-center gap-2 pl-3 border-l border-gray-300">
                {{! Hide Legacy Frame Toggle }}
                <button
                  type="button"
                  class="p-2 rounded-lg transition-colors
                    {{if
                      this.isLegacyFrameHidden
                      'bg-amber-100 text-amber-700'
                      'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }}"
                  title="{{if this.isLegacyFrameHidden 'Show legacy frame' 'Hide legacy frame'}}"
                  {{on "click" this.toggleLegacyFrame}}
                >
                  {{#if this.isLegacyFrameHidden}}
                    <PhEye @size={{20}} />
                  {{else}}
                    <PhEyeSlash @size={{20}} />
                  {{/if}}
                </button>

                {{! Demo Mode Toggle }}
                <button
                  type="button"
                  class="p-2 rounded-lg transition-colors
                    {{if
                      this.isDemoMode
                      'bg-violet-100 text-violet-700'
                      'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }}"
                  title="{{if this.isDemoMode 'Disable demo mode' 'Enable demo mode'}}"
                  {{on "click" this.toggleDemoMode}}
                >
                  <PhSparkle @size={{20}} />
                </button>

                {{! Data Audit Toggle }}
                <button
                  type="button"
                  class="p-2 rounded-lg transition-colors
                    {{if
                      this.reimaginedData.isDataAuditEnabled
                      'bg-rose-100 text-rose-700'
                      'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }}"
                  title="{{if
                    this.reimaginedData.isDataAuditEnabled
                    'Disable data audit'
                    'Enable data audit'
                  }}"
                  {{on "click" this.reimaginedData.toggleDataAudit}}
                >
                  <PhWarning @size={{20}} />
                </button>

                {{! Component Review Toggle }}
                <button
                  type="button"
                  class="p-2 rounded-lg transition-colors
                    {{if
                      this.componentReview.isReviewModeEnabled
                      'bg-violet-100 text-violet-700'
                      'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }}"
                  title="{{if
                    this.componentReview.isReviewModeEnabled
                    'Disable component review'
                    'Enable component review'
                  }}"
                  {{on "click" this.componentReview.toggleReviewMode}}
                >
                  <PhChatCircleDots @size={{20}} />
                </button>

                {{! Dark Mode Toggle }}
                <button
                  type="button"
                  class="p-2 rounded-lg transition-colors
                    {{if
                      this.isDarkMode
                      'bg-indigo-100 text-indigo-700'
                      'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }}"
                  title="{{if this.isDarkMode 'Switch to light mode' 'Switch to dark mode'}}"
                  {{on "click" this.toggleDarkMode}}
                >
                  {{#if this.isDarkMode}}
                    <PhSun @size={{20}} />
                  {{else}}
                    <PhMoon @size={{20}} />
                  {{/if}}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Header>

      {{! Reimagined Navigation - Mega-menu with dropdowns }}
      <MegaMenuNav
        @scenarioId={{this.selectedScenarioId}}
        @isFullscreen={{this.isLegacyFrameHidden}}
      />

      {{! Main content area - constrained width with proper overflow }}
      <div class="flex-1 overflow-y-auto overflow-x-hidden min-w-0 bg-white">
        <div
          class="w-full pt-2 pb-6 {{if this.isLegacyFrameHidden 'px-4' 'max-w-7xl mx-auto px-4'}}"
        >
          {{outlet}}
        </div>
      </div>

      {{! Data Source Documentation Panel - dev only }}
      <DataSourceDocs
        @isOpen={{this.reimaginedData.isDocsOpen}}
        @onClose={{this.reimaginedData.closeDocs}}
        @sectionId={{this.reimaginedData.docsSectionId}}
      />

      {{! Component Review Thread Panel }}
      <ReviewDrawer
        @isOpen={{this.componentReview.isDrawerOpen}}
        @onClose={{this.componentReview.closeDrawer}}
        @reviewId={{this.componentReview.activeReviewId}}
        @pagePath={{this.componentReview.activePagePath}}
      />

      {{! Reimagined Toast Notifications }}
      <ToastContainer @toasts={{this.toast.toasts}} @onDismiss={{this.toast.dismiss}} />
    </VStack>
  </template>
}
